import { DatePipe } from '@angular/common';
import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { deepClone } from '@ptg-shared/utils/common.util';
import { Observable } from 'rxjs';
import {
  AnnualCertificationInformation,
  CertificationHistoryList,
  CertificationHistoryQuery,
  DocuSignDocument,
  SigneeResponse,
} from 'src/app/employer/models/annual-certification.model';
import { environment } from 'src/environments/environment';
import {
  AnnualCertificationConfirmationData,
  AnnualCertificationParticipant,
  AnnualCertificationParticipantList,
  AnnualCertificationParticipantsQuery,
  AnnualMetRequirementsSaveRequest,
  CompleteAnnualCertificationBody,
  ConfirmSignaturesRequestBody,
} from '../models/annual-certification-participants.model';
import { SKIP_ERRORS } from '@ptg-interceptor/httpconfig.interceptor';


@Injectable({
  providedIn: 'root',
})
export class AnnualCertificationService {
  annualCertificationFile?: File;
  datePipe = new DatePipe('en-US');

  constructor(
    private httpClient: HttpClient,
  ) {}

  getAnnualCertificationInformation(municipalityId: string, annualCertId?: string): Observable<AnnualCertificationInformation> {
    let params = new HttpParams({ fromObject: { municipalityId } });
    if (annualCertId) {
      params = params.append('annualCertId', annualCertId);
    }
    return this.httpClient.get<AnnualCertificationInformation>(`${ environment.apiUrl }/employer-portal/annual-certification`, { params });
  }

  getCertificationHistoryList(query: CertificationHistoryQuery) {
    let params = new HttpParams();
    if (query.municipalityId) {
      params.append('Municipality', query.municipalityId);
    }

    if (query.totalPerPage && query.pageIndex) {
      params = params.append('PageSize', query.totalPerPage);
      params = params.append('PageIndex', query.pageIndex);
    }

    if (query.sortField) {
      params = params.append(
        'SortNames',
        query.sortField[0].toUpperCase() + query.sortField.substring(1)
      );
      params = params.append('SortType', query.sortType ? query.sortType : 0);
    }

    return this.httpClient.get<CertificationHistoryList>(
      `${environment.apiUrl}/employer-portal/annual-certifications/${query.municipalityId}/AnnualCertificationHistory`,
      { params }
    );
  }

  getAnnualCertificationParticipants(
    annualCertId: string,
    query: AnnualCertificationParticipantsQuery,
  ): Observable<AnnualCertificationParticipantList> {
    const newQuery = deepClone(query) as any;
    if (newQuery.sortNames === null) {
      delete newQuery['sortNames'];
    }
    const params = new HttpParams({ fromObject: newQuery as any });
    return this.httpClient.get<AnnualCertificationParticipantList>(
      `${ environment.apiUrl }/employer-portal/${ annualCertId }/annual-certification-participants`,
      { params },
    );
  }

  updateEndOfService(
    annualCertificationParticipant: AnnualCertificationParticipant
  ): Observable<void> {
    const endOfServiceParam = {
      endOfService: this.datePipe.transform(annualCertificationParticipant.endOfService, 'yyyy-MM-dd'),
      clientDate: this.datePipe.transform(new Date(), 'YYYY-MM-dd'),
    };
    return this.httpClient.post<void>(
      `${environment.apiUrl}/employer-portal/${annualCertificationParticipant.annualCertificationId}/${annualCertificationParticipant.id}/end-of-service`,
      endOfServiceParam
    );
  }

  updateMetRequirement(
    annualCertificationParticipant: AnnualCertificationParticipant
  ): Observable<void> {
    const metRequirementParam = {
      isMet: annualCertificationParticipant.metRequirement,
    };
    const timeStamp = new Date().getTime();
    return this.httpClient.post<void>(
      `${environment.apiUrl}/employer-portal/${annualCertificationParticipant.annualCertificationId}/${annualCertificationParticipant.id}/met-requirement?timeStamp=${timeStamp}`,
      metRequirementParam
    );
  }

  checkAllMetRequirement(annualCertId: string) {
    return this.httpClient.get(`${ environment.apiUrl }/employer-portal/annual-certifications/${annualCertId}/check-all-met-requirement`)
  }

  uploadFile(annualCertId: string) {
    const formData = new FormData();
    formData.append('File', this.annualCertificationFile || null as any);
    return this.httpClient.post<void>(`${environment.apiUrl}/Municipality/${ annualCertId }/upload-annual-certification`, formData);
  }

  saveMetRequirements(annualCertId: string, annualMetSaveRequest: AnnualMetRequirementsSaveRequest) {
    return this.httpClient.post(`${ environment.apiUrl }/employer-portal/${ annualCertId }/save-met-requirements`, annualMetSaveRequest);
  }

  completeAnnualCertification(annualCertId: string, completeAnnualCertificationBody?: CompleteAnnualCertificationBody) {
    return this.httpClient.post(`${ environment.apiUrl }/employer-portal/${annualCertId}/complete-annual-certification`, completeAnnualCertificationBody ?? {});
  }

  verifyCashJournal(cashJournal: string, IsTransaction: boolean = false) {
    return this.httpClient.post(`${ environment.apiUrl }/municipality/verify-cash-journal`, { cashJournal, IsTransaction });
  }

  confirmationBeforePostingAnnualCertification(annualCertId: string) {
    const context = new HttpContext().set(SKIP_ERRORS, '500');
    return this.httpClient.get<AnnualCertificationConfirmationData>(`${ environment.apiUrl }/municipality/${annualCertId}/confirmation-before-posting-annual-certification`, { context });
  }

  setConfirmSignatures(confirmSignaturesRequestBody: ConfirmSignaturesRequestBody) {
    return this.httpClient.post<AnnualCertificationConfirmationData>(`${ environment.apiUrl }/municipality/annual-certification-action-confirm-signatures`, confirmSignaturesRequestBody);
  }

  getListSignee(municipalityId: string): Observable<SigneeResponse> {
    return this.httpClient.get<SigneeResponse>(`${ environment.apiUrl }/employer-portal/${ municipalityId }/signers`);
  }

  sendSigneeToDocuSign(body: DocuSignDocument) {
    return this.httpClient.post(`${ environment.apiUrl }/api/e-signing/send-signing-envelope`, body);
  }

  sendReminderToDocuSign(documentId: string) {
    return this.httpClient.post(`${ environment.apiUrl }/api/e-signing/send-reminder-envelope/${documentId}`, null);
  }

  updateSignee(body: DocuSignDocument) {
    return this.httpClient.put(`${ environment.apiUrl }/api/e-signing/${body.documentId}/signer`, body);
  }

  checkEmailContactChange(documentId: string): Observable<{isChanged: boolean}> {
    return this.httpClient.get<{isChanged: boolean}>(`${ environment.apiUrl }/api/e-signing/${documentId}/signer/is-changed`);
  }
}
